import { getToken, setToken, removeToken } from '../utils/tokenStore';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import permissionService from '../services/permissionService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('lis_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [accessToken, setAccessToken] = useState(() => getToken('lis_access_token'));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const logout = useCallback(async () => {
    const refreshToken = getToken('lis_refresh_token');
    const loginDetailId = localStorage.getItem('lis_login_detail_id');
    await authService.logout(refreshToken, loginDetailId ? parseInt(loginDetailId, 10) : null);
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem('lis_user');
    removeToken('lis_access_token');
    removeToken('lis_refresh_token');
    localStorage.removeItem('lis_login_detail_id');
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(null);
      setAccessToken(null);
      localStorage.removeItem('lis_user');
      removeToken('lis_access_token');
      removeToken('lis_refresh_token');
      localStorage.removeItem('lis_login_detail_id');
    };

    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, []);

  // Hydrate & verify user session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken('lis_access_token');
      if (token) {
        try {
          const res = await authService.getMe();
          if (res.success && res.data) {
            setUser(res.data);
            localStorage.setItem('lis_user', JSON.stringify(res.data));
          } else {
            await logout();
          }
        } catch (err) {
          if (err.response?.status === 401 || err.response?.status === 403 || !getToken('lis_access_token')) {
            await logout();
          }
        }
      } else {
        try {
          const res = await authService.getMe();
          if (res.success && res.data) {
            setUser(res.data);
            localStorage.setItem('lis_user', JSON.stringify(res.data));
          } else {
            setUser(null);
          }
        } catch (err) {
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [logout]);

  const login = async (username, password, centreId = null, roleId = null) => {
    setError(null);
    try {
      const res = await authService.login(username, password, centreId, roleId);
      if (res.success && res.data) {
        if (res.data.requiresWorkspaceSelection) {
          const token = res.data.accessToken;
          if (token) {
            setToken('lis_access_token', token);
            setAccessToken(token);
          }
          return {
            success: true,
            requiresWorkspaceSelection: true,
            workspaces: res.data.workspaces || []
          };
        }

        const { accessToken: token, refreshToken, user: userData, loginDetailId } = res.data;
        if (token) {
          setToken('lis_access_token', token);
          setAccessToken(token);
        }
        if (refreshToken) {
          setToken('lis_refresh_token', refreshToken);
        }
        if (userData) {
          localStorage.setItem('lis_user', JSON.stringify(userData));
          setUser(userData);
        }
        if (loginDetailId) {
          localStorage.setItem('lis_login_detail_id', loginDetailId.toString());
        }

        return { success: true, user: userData, requiresWorkspaceSelection: false };
      } else {
        const msg = res.message || 'Authentication failed.';
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        err.message ||
        'Unable to connect to LIS server.';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  const switchWorkspace = useCallback(async (centreId, roleId) => {
    try {
      const res = await permissionService.switchWorkspace(centreId, roleId);
      if (res.success && res.data) {
        const { accessToken: token, refreshToken, user: userData } = res.data;
        if (token) {
          setToken('lis_access_token', token);
          setAccessToken(token);
        }
        if (refreshToken) {
          setToken('lis_refresh_token', refreshToken);
        }
        if (userData) {
          localStorage.setItem('lis_user', JSON.stringify(userData));
          setUser(userData);
        }
        return { success: true, user: userData };
      }
      return { success: false, message: res.message || 'Failed to switch workspace.' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message };
    }
  }, []);

  const hasRole = (role) => {
    if (!user || !user.roleName) return false;
    if (user.roleName.toUpperCase() === 'ADMINISTRATOR' || user.roleId === 1) return true;
    return user.roleName.toUpperCase() === role.toUpperCase();
  };

  const hasPermission = (permissionKey) => {
    if (!user || !user.permissions) return false;
    if (user.roleName?.toUpperCase() === 'ADMINISTRATOR' || user.roleId === 1) return true;
    return !!user.permissions[permissionKey];
  };

  const isScreenLicensed = useCallback((screenCode) => {
    if (!user) return false;
    if (!screenCode || screenCode.toUpperCase() === 'DASHBOARD') return true;
    if (screenCode.toUpperCase() === 'SAAS_PLATFORM') {
      return Boolean(
        user.isPlatformAdmin ||
        user.tenantId === 1 ||
        ['PLATFORM_ADMIN', 'SUPER_ADMIN', 'SYSTEM_ADMIN'].includes(user.roleName?.toUpperCase())
      );
    }
    if (user.license && Array.isArray(user.license.licensedScreens)) {
      return user.license.licensedScreens.some(
        s => s.toUpperCase() === screenCode.toUpperCase()
      );
    }
    return true; // Default fallback if license payload is not yet loaded
  }, [user]);

  const isElementLicensed = useCallback((elementKey) => {
    if (!user || !elementKey) return true;
    if (user.license && Array.isArray(user.license.licensedElements)) {
      return user.license.licensedElements.some(
        e => e.toUpperCase() === elementKey.toUpperCase()
      );
    }
    return true; // Default fallback
  }, [user]);

  // Fine-grained screen & action permission checker: can('REGISTRATION', 'CREATE')
  const can = useCallback((screenCode, actionCode = 'VIEW') => {
    if (!user) return false;

    // 1. First Gate: Subscription Licensing Gate (Tenant Level)
    // If the hospital didn't purchase the module, no role (even local Administrator) can access it
    if (!isScreenLicensed(screenCode)) {
      return false;
    }

    // 2. Second Gate: Role & Permission Gate (User Level)
    if (user.roleName?.toUpperCase() === 'ADMINISTRATOR' || user.roleId === 1) return true;
    if (!user.effectivePermissions) return false;

    const screenKey = Object.keys(user.effectivePermissions).find(
      k => k.toLowerCase() === screenCode.toLowerCase()
    );
    if (!screenKey) return false;

    const actionMap = user.effectivePermissions[screenKey];
    const actionKey = Object.keys(actionMap).find(
      k => k.toLowerCase() === actionCode.toLowerCase()
    );
    if (!actionKey) return false;

    return !!actionMap[actionKey];
  }, [user, isScreenLicensed]);


  const loginWithPasskey = async (passkeyResponseData) => {
    setError(null);
    try {
      if (passkeyResponseData.requiresWorkspaceSelection) {
        const token = passkeyResponseData.accessToken;
        if (token) {
          setToken('lis_access_token', token);
          setAccessToken(token);
        }
        return {
          success: true,
          requiresWorkspaceSelection: true,
          workspaces: passkeyResponseData.workspaces || [],
        };
      }

      const { accessToken: token, refreshToken, user: userData, loginDetailId } = passkeyResponseData;
      if (token) {
        setToken('lis_access_token', token);
        setAccessToken(token);
      }
      if (refreshToken) {
        setToken('lis_refresh_token', refreshToken);
      }
      if (userData) {
        localStorage.setItem('lis_user', JSON.stringify(userData));
        setUser(userData);
      }
      if (loginDetailId) {
        localStorage.setItem('lis_login_detail_id', loginDetailId.toString());
      }

      return { success: true, user: userData, requiresWorkspaceSelection: false };
    } catch (err) {
      const msg = err.message || 'Passkey session creation failed.';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isAuthenticated: !!user || !!accessToken,
        isLoading,
        error,
        login,
        loginWithPasskey,
        logout,
        switchWorkspace,
        hasRole,
        hasPermission,
        can,
        isScreenLicensed,
        isElementLicensed,
        license: user?.license,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

